import type { PlaygroundNodeData } from "@/utils/playground/workflowToNodes";

import React from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Icon } from "@iconify/react";
import { Chip, Tooltip } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useTheme } from "@/hooks/use-theme";

import { StatusBadge } from "./StatusBadge";

const PlaygroundAgentNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as PlaygroundNodeData;
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const model = nodeData.parameters?.model as string | undefined;
  const tools = (nodeData.parameters?.tools as string[]) || [];
  const agentType = nodeData.agentType || "chat_agent";
  const validationErrors = nodeData.validationErrors || [];
  const hasErrors = validationErrors.length > 0;

  const typeLabel = agentType.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
  const status = nodeData.status;

  const getBorderClass = () => {
    if (hasErrors) return isDark ? "border-red-500" : "border-red-400";
    if (status === "running") return "border-warning animate-pulse";
    if (status === "success") return "border-success";
    if (status === "error") return "border-danger";

    return isDark ? "border-blue-500/50" : "border-blue-300";
  };

  return (
    <div
      className={`
        relative px-4 py-3 rounded-xl border-2 min-w-[220px] max-w-[280px] transition-all duration-200
        ${selected ? "ring-2 ring-blue-400 ring-offset-2" : ""}
        ${isDark ? "bg-default-100" : "bg-white"}
        ${getBorderClass()}
        shadow-md hover:shadow-lg
      `}
    >
      <div className="flex items-center gap-3">
        <div
          className={`
            rounded-full w-10 h-10 flex items-center justify-center shrink-0
            ${isDark ? "bg-blue-500/20" : "bg-blue-50"}
          `}
        >
          <Icon
            className={`w-5 h-5 ${isDark ? "text-blue-400" : "text-blue-600"}`}
            icon="solar:user-hand-up-outline"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm font-semibold truncate ${isDark ? "text-default-800" : "text-blue-900"}`}
          >
            {nodeData.label}
          </div>
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            <Chip className="text-tiny" color="primary" size="sm" variant="flat">
              {typeLabel}
            </Chip>
            {model && (
              <Chip className="text-tiny" color="default" size="sm" variant="flat">
                {model}
              </Chip>
            )}
          </div>
        </div>
      </div>

      {tools.length > 0 && (
        <div className="mt-2 flex items-center gap-1">
          <Icon
            className={`w-3.5 h-3.5 ${isDark ? "text-default-500" : "text-gray-400"}`}
            icon="qlementine-icons:tool-16"
          />
          <span className={`text-xs ${isDark ? "text-default-500" : "text-gray-500"}`}>
            {tools.length} {tools.length === 1 ? t("tool") : t("tools")}
          </span>
        </div>
      )}

      {hasErrors && (
        <Tooltip
          content={
            <div className="max-w-xs">
              {validationErrors.map((err, i) => (
                <p key={i} className="text-xs">
                  {err}
                </p>
              ))}
            </div>
          }
        >
          <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
            <Icon className="w-3 h-3 text-white" icon="solar:danger-triangle-linear" />
          </div>
        </Tooltip>
      )}

      {nodeData.status && !hasErrors && (
        <div className="absolute -top-2 -right-2">
          <StatusBadge isCurrentStep={nodeData.isCurrentStep || false} status={nodeData.status} />
        </div>
      )}

      <Handle
        className="!w-3 !h-3 !bg-primary-500 !border-2 !border-white"
        position={Position.Left}
        type="target"
      />
      <Handle
        className="!w-3 !h-3 !bg-primary-500 !border-2 !border-white"
        position={Position.Right}
        type="source"
      />
    </div>
  );
};

export default PlaygroundAgentNode;
